import { type SingleValue } from "react-select";
import type { Option, SharedAsyncSelectProps } from "./internal/types";
import useTranslation from "@hooks/use_translation";
import { SelectWrapper } from "./internal/select_wrapper";
import { useSharedReactSelectProps } from "./internal/hooks/use_shared_react_select_props";
import { useAsyncReactSelectProps } from "./internal/hooks/use_async_react_select_props";
import { useReactSelect } from "./internal/hooks/use_react_select";

interface AsyncSingleSelectProps extends SharedAsyncSelectProps {
  onChange: (value: Option | null) => void;
  value: Option | null;
}

export const AsyncSingleSelect = ({
  clearable = false,
  creatable = false,
  disabled = false,
  error,
  helperText,
  label,
  loadOptions,
  onChange,
  placeholder,
  required = false,
  value,
  ...dataProps
}: AsyncSingleSelectProps) => {
  const { t } = useTranslation("sprout");
  const { id, ...sharedAsyncReactSelectProps } = useSharedReactSelectProps(
    (newValue) => onChange(newValue as SingleValue<Option>),
    { error, label },
  );
  const asyncReactSelectProps = useAsyncReactSelectProps(loadOptions);
  const ReactSelect = useReactSelect({ creatable });

  return (
    <SelectWrapper
      selectId={id}
      label={label}
      error={error}
      helperText={helperText}
      disabled={disabled}
      {...dataProps}
      required={required}
    >
      <ReactSelect
        id={id}
        {...sharedAsyncReactSelectProps}
        isClearable={clearable}
        isDisabled={disabled}
        placeholder={placeholder || t("select.placeholder")}
        value={value}
        {...asyncReactSelectProps}
      />
    </SelectWrapper>
  );
};
